import { useEffect } from 'react';

import { UseQueryLightError } from '@atlaskit/teams-client';

import { useAnalytics } from '../components/AnalyticsContext';

import { type UseTrackerStopOptions } from './types';

export function useExpTrackerStop(options: UseTrackerStopOptions): void {
	const { name, error, checkShouldAbort } = options;

	const analytics = useAnalytics();
	const tracker = analytics.expTracker();

	useEffect(() => {
		const triggerStopEvent = () => {
			if (!error) {
				return;
			}

			// If error has a statusCode of 429, abort instead.
			if (isRateLimitError(error)) {
				abortExpTracker(`Ignoring 429 error: ${error.message}`);
				return;
			}

			tracker.stopOnError({
				name,
				error,
			});
		};

		const abortExpTracker = (reason: string) => {
			tracker.abort({
				name,
				reason,
			});
		};

		if (error) {
			const reason = checkShouldAbort ? checkShouldAbort(error) : undefined;
			if (typeof reason === 'string' || isRateLimitError(error)) {
				abortExpTracker(reason ?? `Ignoring 429 error: ${error.message}`);
			} else {
				triggerStopEvent();
			}
		}
	}, [checkShouldAbort, error, name, tracker]);
}

const isRateLimitError = (error: Error): boolean => {
	if (error instanceof UseQueryLightError && error.networkError) {
		return isRateLimitError(error.networkError);
	}
	return (
		('statusCode' in error && error.statusCode === 429) ||
		('status' in error && error.status === 429) ||
		error?.message?.includes('429')
	);
};
